<template>
  <SliderModalItem>
    <SliderModalContainer
      :modal-title="$t('products.filters.brands')"
      modal-header-animation="teleport"
      modal-close-btn-icon="chevron"
      :modal-clear-btn-text="
        selectedFilterParams.brands.length ? $t('products.filters.clear') : ''
      "
      @clear="clearSelectedFilterParam('brands')"
    >
      <template #slider-modal-trigger-btn>
        <SliderModalNavBtn
          btn-title="Brands"
          :btn-text="formatSelection(selectedFilterParams.brands)"
        />
      </template>

      <template #slider-modal-content>
        <div class="search-container">
          <UiInput
            type="filter"
            name="filter-brands"
            placeholder="Search brands"
            value=""
            :cta-button="{ icon: 'search' }"
            @input="setSearchInput"
          />
        </div>

        <SliderModalItem
          v-for="(brand, index) in filteredBrands"
          :key="brand['id']"
        >
          <UiCheckbox
            :id="brand['id']"
            v-model="selectedFilterParams.brands"
            class="body-m"
            :label="brand['name']"
            :label-position="LabelPositionOpts.LEFT"
            :native-value="brand['slugUrl']"
            @update:model-value="onFilterUpdate($event, brand, index)"
          />
        </SliderModalItem>
      </template>
    </SliderModalContainer>
  </SliderModalItem>
</template>

<script lang="ts" setup>
import { type BrandFilters } from '~/types/api/data/product/filter/filter.types'
import SliderModalContainer from './slider-modal-container.vue'
import SliderModalItem from './slider-modal-item.vue'
import SliderModalNavBtn from './slider-modal-nav-btn.vue'
import UiCheckbox, { LabelPositionOpts } from '@sephora-asia/ui-checkbox'
import UiInput from '@sephora-asia/ui-input/UiInput.vue'
import { toNormalizeForm } from '~/utils/string-helpers'
import useFilterTrackingEvent from '~/composables/utils/product-listing/filter/use-product-listing-filter-tracking'

interface Props {
  brandFilters: BrandFilters[]
}
const props = defineProps<Props>()

const DELIMITER = '|' // For regex search

const searchInput = ref('')
const stringedKeys = ref('') // Brand slugs in string for regex search
const brandsObj = ref<{ [key: string]: BrandFilters }>({}) // For easier access to brand attributes

const { selectedFilterParams, clearSelectedFilterParam } =
  useProductListingFiltersStore()

const { trackFilterClicked } = useFilterTrackingEvent()

onMounted(() => {
  initRefs()
})

watch(
  () => props.brandFilters,
  () => {
    initRefs()
  }
)

/**
 * ----- Computed -----
 */
const filteredBrands = computed(() => {
  if (!searchInput.value) return props.brandFilters

  const regex = new RegExp(
    `[^${DELIMITER}]*${searchInput.value}[^${DELIMITER}]*`,
    'g'
  )
  const filteredKeys = stringedKeys.value.match(regex) ?? []

  return filteredKeys.map(key => brandsObj.value[key])
})

/**
 * ----- Methods -----
 */
function initRefs() {
  brandsObj.value = props.brandFilters.reduce(
    (obj: { [key: string]: BrandFilters }, brand) => {
      obj[toNormalizeForm(brand.slugUrl).toLowerCase()] = brand
      return obj
    },
    {}
  )

  stringedKeys.value = Object.keys(brandsObj.value).join(DELIMITER)
}

function formatSelection(selection: string[]) {
  if (!selection.length) return
  return selection.map(slug => brandsObj.value[slug]?.name).join(', ')
}

function setSearchInput(event: string | number) {
  searchInput.value = toNormalizeForm(event.toString())
    .toLowerCase()
    .trim()
    .replace(/\s/g, '-')
}

function onFilterUpdate(event: string[], brand: BrandFilters, index: number) {
  trackFilterClicked(event, {
    value: brand.name,
    slugUrl: brand.slugUrl,
    filterIndex: index,
    filterCategoryIndex: 0,
    type: 'brand',
  })
}
</script>

<style lang="scss" scoped>
// For UiCheckbox label
label {
  width: inherit;
  height: inherit;
  align-items: center;
}

.search-container {
  padding: 16px;
}
</style>
